import { io, Socket } from "socket.io-client";
import { BACKEND_URL } from "../api/endpoints";

type ServerToClientEvents = {
  "auth:ok": (data: unknown) => void;
  "chat:message": (msg: any) => void;
  "webrtc:offer": (payload: { sdp: RTCSessionDescriptionInit }) => void;
  "webrtc:answer": (payload: { sdp: RTCSessionDescriptionInit }) => void;
  "webrtc:ice-candidate": (payload: { candidate: RTCIceCandidateInit }) => void;
  "call:incoming": (payload: { sessionId: string; callerName: string; emotion: string; method: string; skills?: string[]; languages?: string[] }) => void;
  "call:accepted": (payload: { sessionId: string; listenerName?: string; listenerId?: string }) => void;
  "call:rejected": (payload: { sessionId: string }) => void;
  "call:unavailable": (payload: { sessionId: string }) => void;
  "session:new": (data: { session: any; message?: string }) => void;
  "session:assigned": (data: { session: any }) => void;
  "session:pending_available": (data: { session: any }) => void;
  "listener:status": (data: { userId?: string; listenerId?: string; status: string }) => void;
  "listener:status-change": (data: {
    listenerId?: string;
    userId?: string;
    status: string;
    isAvailable?: boolean;
    isOnline?: boolean;
    isBusy?: boolean;
    legacyStatus?: string;
    lastSeen?: number;
  }) => void;
  "listener:online-list": (userIds: string[]) => void;
  "presence:heartbeat-ack": (data: { timestamp: number }) => void;
  "user:presence-change": (data: {
    userId?: string;
    listenerId?: string;
    status: string;
    isAvailable?: boolean;
    isOnline?: boolean;
    isBusy?: boolean;
    legacyStatus?: string;
    lastSeen?: number | string;
    role?: string;
  }) => void;
  "presence:update": (data: {
    userId?: string;
    listenerId?: string;
    status: string;
    isAvailable?: boolean;
    isOnline?: boolean;
    isBusy?: boolean;
    legacyStatus?: string;
    lastSeen?: number | string;
    role?: string;
  }) => void;
};

type ClientToServerEvents = {
  "session:join": (
    payload: { sessionId: string },
    ack?: (resp: { ok: boolean; roomId?: string; sessionId?: string; callStatus?: string; error?: string }) => void
  ) => void;
  "chat:send": (
    payload: { roomId: string; text: string },
    ack?: (resp: { ok: boolean; error?: string }) => void
  ) => void;
  "webrtc:offer": (payload: { roomId: string; sdp: RTCSessionDescriptionInit }) => void;
  "webrtc:answer": (payload: { roomId: string; sdp: RTCSessionDescriptionInit }) => void;
  "webrtc:ice-candidate": (payload: { roomId: string; candidate: RTCIceCandidateInit }) => void;
  "call:initiate": (payload: { sessionId: string; targetListenerId?: string; callerName: string; emotion: string; method: string; skills?: string[]; languages?: string[] }) => void;
  "call:accept": (payload: { sessionId: string; listenerName?: string; listenerId?: string }) => void;
  "listener:set-availability": (payload: { isAvailable: boolean; listenerId?: string }, ack?: (resp: { ok: boolean; isAvailable: boolean; status: string }) => void) => void;
  "listener:request-online-list": (ack?: (userIds: string[]) => void) => void;
  "listener:get-statuses": (ack?: (statuses: Record<string, string>) => void) => void;
  "admin:set-listener-status": (payload: { listenerId: string; userId?: string; isOnline: boolean; status: string }, ack?: (resp: any) => void) => void;
  "presence:heartbeat": () => void;
  "presence:disconnecting": () => void;
};

let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
let currentToken: string | null = null;

export function getSocket(token: string) {
  if (socket && currentToken === token) return socket;

  if (socket) {
    try {
      socket.disconnect();
    } catch {
      // ignore
    }
    socket = null;
  }

  currentToken = token;
  socket = io(BACKEND_URL, {
    auth: { token },
    transports: ["websocket"],
  });

  return socket;
}

export function disconnectSocket() {
  if (!socket) return;
  try {
    socket.disconnect();
  } finally {
    socket = null;
    currentToken = null;
  }
}

